import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import {
  isUserLoggedIn, getUserData, getUserRole, getHomeRouteForLoggedInUser,
} from '@/auth/utils'

Vue.use(VueRouter)

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    { path: '/', redirect: { name: 'login' } },
    {
      path: '/admin',
      meta: {
        needLogin: true,
      },
      beforeEnter: (to, from, next) => {
        store.dispatch('auth/checkToken')
        next()
      },
      component: () => import('@/layouts/full/LayoutFull.vue'),
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/views/Home.vue'),
        },
        {
          path: 'profile',
          name: 'profile',
          component: () => import('@/views/account/AccountPage.vue'),
        },
        {
          path: 'traceabilities',
          meta: {
            needLogin: true,
            roleAllow: ['super_admin', 'admin'],
          },
          name: 'traceabilities',
          component: () => import('@/views/users/UpdateJourneyLog.vue'),
        },
        {
          path: 'users/operarios',
          meta: {
            needLogin: true,
            roleAllow: ['super_admin'],
          },
          name: 'operarios',
          component: () => import('@/views/users/Empleados.vue'),
        },
        {
          path: 'users',
          meta: {
            needLogin: true,
            roleAllow: ['super_admin', 'admin'],
          },
          name: 'users',
          component: () => import('@/views/users/List.vue'),
        },
        {
          path: 'users/access-log',
          meta: {
            needLogin: true,
            roleAllow: ['super_admin', 'admin_empresa'],
          },
          name: 'users',
          component: () => import('@/views/users/AccessLog.vue'),
        },
        {
          path: 'users/edit/:id',
          meta: {
            needLogin: true,
            roleAllow: ['super_admin', 'admin'],
          },
          name: 'editUser',
          component: () => import('@/views/users/Edit.vue'),
        },
        {
          path: 'users/view/:id',
          meta: {
            needLogin: true,
            roleAllow: ['super_admin', 'admin'],
          },
          name: 'viewUser',
          component: () => import('@/views/users/View.vue'),
        },
        {
          path: 'users/create',
          meta: {
            needLogin: true,
            roleAllow: ['super_admin', 'admin'],
          },
          name: 'createUser',
          component: () => import('@/views/users/Create.vue'),
        },
        {
          path: 'departments',
          meta: {
            needLogin: true,
            roleAllow: ['super_admin', 'admin'],
          },
          name: 'departments',
          component: () => import('@/views/departments/List.vue'),
        },
        {
          path: 'departments/edit/:id',
          meta: {
            needLogin: true,
            roleAllow: ['super_admin', 'admin'],
          },
          name: 'editDepartment',
          component: () => import('@/views/departments/Edit.vue'),
        },
        {
          path: 'departments/create',
          meta: {
            needLogin: true,
            roleAllow: ['super_admin', 'admin'],
          },
          name: 'createDepartment',
          component: () => import('@/views/departments/Create.vue'),
        },
        {
          path: 'partes/operario',
          meta: {
            needLogin: true,
            roleAllow: ['super_admin', 'admin', 'operario'],
          },
          name: 'partesOperario',
          component: () => import('@/views/partes/Operario.vue'),
        },
        {
          path: 'calendario',
          meta: {
            needLogin: true,
            roleAllow: ['super_admin', 'admin'],
          },
          name: 'calendario',
          component: () => import('@/views/calendario/Calendario.vue'),
        },
        {
          path: 'config',
          meta: {
            needLogin: true,
            roleAllow: ['super_admin', 'admin'],
          },
          name: 'config',
          component: () => import('@/views/clients/Edit.vue'),
        },
        {
          path: 'policy',
          meta: {
            needLogin: true,
            roleAllow: ['super_admin', 'admin'],
          },
          name: 'policy',
          component: () => import('@/views/configuracion/Policy.vue'),
        },
        {
          path: 'policy/view',
          meta: {
            needLogin: true,
            roleAllow: ['super_admin', 'admin', 'operario'],
          },
          name: 'viewPolicy',
          component: () => import('@/views/configuracion/ViewPolicy.vue'),
        },
        {
          path: "clients/create",
          meta: {
            needLogin: true,
            roleAllow: ["super_admin"],
          },
          name: "createClient",
          component: () => import("@/views/clients/Create.vue"),
        },
      ],
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/Login.vue'),
      meta: {
        layout: 'full',
        redirectIfLoggedIn: true,
      },
    },
    {
      path: '/reset-password/:token/:email',
      name: 'reset-password',
      component: () => import('@/views/users/ResetPassword.vue'),
      meta: {
        layout: 'full',
        redirectIfLoggedIn: true,
      },
    },
    {
      path: '/reset-password-sms/:username',
      name: 'reset-password-sms',
      component: () => import('@/views/users/ResetPasswordSms.vue'),
      meta: {
        layout: 'full',
        redirectIfLoggedIn: true,
      },
    },
    {
      path: '/error-404',
      name: 'error-404',
      component: () => import('@/views/error/Error404.vue'),
      meta: {
        layout: 'full',
      },
    },
    {
      path: '/error-401',
      name: 'error-401',
      component: () => import('@/views/error/Error404.vue'),
      meta: {
        layout: 'full',
      },
    },
    {
      path: '*',
      redirect: 'error-404',
    },
  ],
})

router.beforeEach((to, _, next) => {
  const isLoggedIn = isUserLoggedIn()

  if (to.meta.needLogin) {
    if (!isLoggedIn) { return next({ name: 'login' }) }
  }

  // Redirect if logged in
  if (to.meta.redirectIfLoggedIn === true && isLoggedIn) {
    const userData = getUserData()
    next(getHomeRouteForLoggedInUser(userData))
  }
  if (to.meta.roleAllow) {
    const role = getUserRole()
    if (!to.meta.roleAllow.includes(role)) {
      next({ name: 'error-401' })
    }
  }

  return next()
})

// ? For splash screen
// Remove afterEach hook if you are not using splash screen
router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

export default router
